import { Injectable } from '@angular/core';
import { suggestionDTO, spellingStateEnum, tokenDTO, sentenceDTO, paragraphDTO } from '@capiche/ts-data-models-shared';
import { CapicheLoggerService } from 'capiche-logger';

const excludedWords = [
  // https://capiche.atlassian.net/jira/software/c/projects/CSW/boards/2?selectedIssue=CSW-612
  // should be sepcified in lower case
  'cunt',
  'cunts',
  'fucked',
  'fucker',
  'fuckers',
  'fuck',
  'fuckhead',
  'fucking',
  'fucks',
  'motherfucker',
  'motherfuckers',
  'motherfucking',
];

@Injectable({
  providedIn: 'root',
})
export class CensorService {
  private logger: CapicheLoggerService = new CapicheLoggerService('censor.service', false);
  private excludedWordsSet = new Set(excludedWords);
  private regex: RegExp;

  constructor() {
    this.regex = this.createCensorRegex(excludedWords);
    this.logger.log('constructor', this.regex);
  }

  /**
   *
   * @param dtoIn array of suggestionDTO
   * @returns array of suggestionDTO with excludedWords removed. In the case where no words remain returns an empty array
   */
  censorSuggestionDTO(dtoIn: suggestionDTO[]): suggestionDTO[] {
    const result: suggestionDTO[] = dtoIn.filter((dto) => !this.excludedWordsSet.has(dto.word.toLocaleLowerCase()));
    this.logger.log('censorSuggestionDTO', result);
    return result;
  }

  /**
   *
   * @param dtoIn tokenDTO
   * @returns tokenDTO with spellings censored.  If all suggestions are censored for a token, the spelling state is set to unknown
   */
  censorTokenDTO(dtoIn: tokenDTO): tokenDTO {
    const suggestions: suggestionDTO[] | undefined = dtoIn.suggestions
      ? this.censorSuggestionDTO(dtoIn.suggestions)
      : dtoIn.suggestions;
    const spellingState = suggestions && suggestions.length > 0 ? dtoIn.spellingState : spellingStateEnum.unknown;

    const result: tokenDTO = { ...dtoIn, suggestions, spellingState };
    this.logger.log('censorTokenDTO', JSON.stringify(result));
    return result;
  }

  /**
   *
   * @param dtoIn sentenceDTO
   * @returns sentenceDTO with suggestions for all tokens censored.
   * sentence.input is unchanged
   * TODO: correctedSentenceString is **currently uncensored**
   */
  censorSentenceDTO(dtoIn: sentenceDTO): sentenceDTO {
    const tokens: tokenDTO[] = dtoIn.tokens.map((token: tokenDTO) => this.censorTokenDTO(token));
    const result: sentenceDTO = { ...dtoIn, tokens };
    this.logger.log('censorSentenceDTO', JSON.stringify(result));
    return result;
  }

  /**
   *
   * @param input paragraphDTO
   * @returns paragraphDTO with suggestions for all tokens censored.
   */
  censorParagraphDTO(input: paragraphDTO): paragraphDTO {
    // TODO: the .input string should also be censored, but this is not currently done
    const sentences: sentenceDTO[] = input.sentences.map((sentence: sentenceDTO) => this.censorSentenceDTO(sentence));
    const result: paragraphDTO = { ...input, sentences };
    this.logger.log('censorParagraphDTO', JSON.stringify(result));
    return result;
  }

  private createCensorRegex(words: string[]): RegExp {
    // match any word that starts with one of the excluded words
    const pattern: string = `\\b(${words.join('|')})\\w*`;
    return new RegExp(pattern, 'gi');
  }

  censorOffensiveWords(text: string): string {
    const result: string = text.replace(this.regex, (match: string, group1: string) => {
      const maskedPrefix = '*'.repeat(group1.length);
      return maskedPrefix + match.slice(group1.length);
    });

    this.logger.log('censorOffensiveWords', text + ' > ' + result);
    return result;
  }

  mumbleOffensiveWords(text: string): string {
    const result: string = text.replace(this.regex, (match: string, group1: string) => {
      return 'beep' + match.slice(group1.length);
    });

    this.logger.log('mumbleOffensiveWords:', result !== text ? `${text} > ${result}` : text);
    return result;
  }
}
